import { AnyAction, combineReducers, configureStore } from '@reduxjs/toolkit';
import { createBrowserHistory } from 'history';
import authReducer from 'modules/auth/store';
import { authSignOut } from 'modules/auth/store/actions';
import { saveState } from 'modules/auth/store/persist';
import masterBoardReducer from 'modules/board-master/store';
import clientChampionshipReducer from 'modules/client/tournaments/store/client-tournament/client-tournament-store';
import chatReducer from 'modules/chat/store';
import devicesReducer from 'modules/devices/store';
import gamesReducer from 'modules/games/store';
import locationsReducer from 'modules/locations/store';
import mainPageReducer from 'modules/main/store/main-page/main-page-store';
import managerReducer from 'modules/manager/store';
import notificationsReducer from 'modules/notifications/store/notifications';
import organizerReducer from 'modules/organizer/store';
import organizersReducer from 'modules/organizers/store';
import overlayV2Reducer from 'modules/overlay-v2/store';

import playersReducer from 'modules/players/store';
import scheduleReducer from 'modules/schedule/store';
import teamsReducer from 'modules/teams/store';
import templatesEditorReducer from 'modules/template-editor/store';
import championshipsReducer from 'modules/championship/store';
import { createReduxHistoryContext } from 'redux-first-history';
import { baseApi } from 'shared/api/base-api';
import { restApi } from 'shared/api/rest-api';

const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: createBrowserHistory(),
});

const appReducer = combineReducers({
  router: routerReducer,
  auth: authReducer,
  organizer: organizerReducer,
  championships: championshipsReducer,
  games: gamesReducer,
  teams: teamsReducer,
  overlayV2: overlayV2Reducer,
  templateEditor: templatesEditorReducer,
  organizers: organizersReducer,
  schedule: scheduleReducer,
  players: playersReducer,
  masterBoard: masterBoardReducer,
  locations: locationsReducer,
  notifications: notificationsReducer,
  clientChampionship: clientChampionshipReducer,
  mainPage: mainPageReducer,
  devices: devicesReducer,
  manager: managerReducer,
  chat: chatReducer,
  [baseApi.reducerPath]: baseApi.reducer,
  [restApi.reducerPath]: restApi.reducer,
});

// При выходе чистим кэши RTK Query, чтобы данные предыдущего пользователя
// (в т.ч. список доступных организаторов) не показывались следующему
export const rootReducer: typeof appReducer = (state, action: AnyAction) => {
  if (action.type === authSignOut.type) {
    const nextState = appReducer(state, action);
    return appReducer(appReducer(nextState, baseApi.util.resetApiState()), restApi.util.resetApiState());
  }

  return appReducer(state, action);
};

// Проверка сериализуемости — без исключений: несериализуемое в Redux не кладём, а уводим
// (`.claude/rules/architecture.md`, «State management»). Её стережёт `redux-serializable-check.test.ts`.
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(routerMiddleware, baseApi.middleware, restApi.middleware),
  devTools: process.env.NODE_ENV !== 'production',
});

store.subscribe(() => {
  saveState(store.getState());
});

export const history = createReduxHistory(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
